// API layer that communicates with the backend server
// All template operations go through /api/templates/* - the server routes to the active storage backend
import {
  Template,
  TemplateConfiguration,
  WorkflowStage,
  WorkflowStageInfo,
  TemplateStatus,
} from '../types'

const API_BASE = '/api'

// Types for lazy-loaded Google Drive folder contents
export interface GDriveFolderInfo {
  id: string
  name: string
}

export interface GDriveFolderContents {
  folderName?: string
  folders: GDriveFolderInfo[]
  files: {
    id: string
    name: string
    fileName: string
    size: number
    fileSize: number
    modifiedTime: string
    createdTime: string
    webViewLink: string
  }[]
}

// Fetch a single folder's immediate children (folders + PDFs)
export async function fetchFolderContents(folderId: string): Promise<GDriveFolderContents> {
  const response = await fetch(`${API_BASE}/gdrive/folder/${encodeURIComponent(folderId)}`)
  if (!response.ok) {
    throw new Error(`Failed to list folder: ${response.statusText}`)
  }
  return response.json()
}

// Fetch current file info from Google Drive (uses the existing resolve endpoint)
export async function fetchRemoteFileInfo(fileId: string): Promise<{
  id: string
  name: string
  mimeType: string
  size: number
  modifiedTime: string
}> {
  // Use the templates endpoint which returns full metadata for gdrive files
  const response = await fetch(`${API_BASE}/templates/${encodeURIComponent(fileId)}`)
  if (!response.ok) {
    throw new Error(`Failed to get remote file info: ${response.statusText}`)
  }
  const data = await response.json()
  return {
    id: fileId,
    name: data.fileName,
    mimeType: 'application/pdf',
    size: data.fileSize,
    modifiedTime: data.lastModified || data.modifiedTime,
  }
}

// Get current user (in real app, from auth context)
const getCurrentUser = () => 'srickus' // Placeholder

// Template Management API
export const templateApi = {
  // Update template progress
  async updateTemplateProgress(
    templateId: string,
    currentStage: WorkflowStage,
    workflowStages: WorkflowStageInfo[],
  ): Promise<Template> {
    const response = await fetch(`${API_BASE}/templates/${templateId}/progress`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        currentStage,
        workflowStages,
      }),
    })

    if (!response.ok) {
      throw new Error('Failed to update template progress')
    }

    return response.json()
  },

  // Helper to determine status from stages
  getStatusFromStages(completedStages: WorkflowStage[]): TemplateStatus {
    if (completedStages.includes(WorkflowStage.EXPORT)) {
      return TemplateStatus.EXPORTED
    }
    if (completedStages.includes(WorkflowStage.REVIEW)) {
      return TemplateStatus.REVIEWED
    }
    if (completedStages.includes(WorkflowStage.FIELDS)) {
      return TemplateStatus.CONFIGURED
    }
    if (completedStages.includes(WorkflowStage.UPLOAD)) {
      return TemplateStatus.UPLOADED
    }
    return TemplateStatus.DRAFT
  },

  // Upload a new PDF template
  async uploadTemplate(
    file: File,
    metadata?: {
      templateLabel?: string
      clientName?: string
      programName?: string
    },
  ): Promise<Template> {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('assignedTo', getCurrentUser())
    formData.append('createdBy', getCurrentUser())

    // Add metadata fields if provided
    if (metadata?.templateLabel) {
      formData.append('templateLabel', metadata.templateLabel)
    }
    if (metadata?.clientName) {
      formData.append('clientName', metadata.clientName)
    }
    if (metadata?.programName) {
      formData.append('programName', metadata.programName)
    }

    const response = await fetch(`${API_BASE}/templates/upload`, {
      method: 'POST',
      body: formData,
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.error || 'Failed to upload template')
    }

    return response.json()
  },

  // Upload a new PDF template from a URL
  async uploadTemplateFromUrl(
    url: string,
    metadata?: {
      templateLabel?: string
      clientName?: string
      programName?: string
    },
  ): Promise<Template> {
    const response = await fetch(`${API_BASE}/templates/upload-from-url`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        url,
        templateLabel: metadata?.templateLabel,
        clientName: metadata?.clientName,
        programName: metadata?.programName,
      }),
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.error || 'Failed to upload from URL')
    }

    return response.json()
  },

  // Get all templates
  async getTemplates(): Promise<Template[]> {
    const response = await fetch(`${API_BASE}/templates`)

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.error || 'Failed to fetch templates')
    }

    return response.json()
  },

  // Get a specific template
  async getTemplate(id: string): Promise<Template | null> {
    try {
      const response = await fetch(`${API_BASE}/templates/${id}`)

      if (!response.ok) {
        return null
      }

      return response.json()
    }
    catch (error) {
      console.error('Error fetching template:', error)
      return null
    }
  },

  // Update template metadata
  async updateTemplate(id: string, updates: Partial<Template>): Promise<Template> {
    const response = await fetch(`${API_BASE}/templates/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(updates),
    })

    if (!response.ok) {
      throw new Error('Failed to update template')
    }

    return response.json()
  },

  // Rename a dashboard column (bulk clientName/programName update)
  async renameColumn(params: {
    field: 'clientName' | 'programName'
    oldValue: string
    newValue: string
    scopeClientName?: string
  }): Promise<{ success: boolean, updated: number }> {
    const response = await fetch(`${API_BASE}/templates/rename-column`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(params),
    })

    if (!response.ok) {
      throw new Error('Failed to rename column')
    }

    return response.json()
  },

  // Delete a template
  async deleteTemplate(id: string): Promise<void> {
    const response = await fetch(`${API_BASE}/templates/${id}`, {
      method: 'DELETE',
    })

    if (!response.ok) {
      throw new Error('Failed to delete template')
    }
  },

  // Download template PDF
  getTemplateDownloadUrl(id: string): string {
    return `${API_BASE}/templates/${id}/pdf`
  },

  // Replace PDF file for existing template (preserving metadata)
  async replacePdf(
    id: string,
    file: File,
  ): Promise<{ template: Template; warnings: string[]; stats: any }> {
    const formData = new FormData()
    formData.append('file', file)

    const response = await fetch(`${API_BASE}/templates/${id}/pdf`, {
      method: 'PUT',
      body: formData,
    })

    if (!response.ok) {
      throw new Error('Failed to replace PDF file')
    }

    return response.json()
  },

  // Clear all AcroForm field values in the working PDF (text, checkboxes,
  // radio groups, dropdowns) — resets baked-in defaults from the imported file
  async clearFieldValues(id: string): Promise<{ clearedCount: number; totalFields: number }> {
    const response = await fetch(`${API_BASE}/templates/${id}/pdf/clear-fields`, {
      method: 'POST',
    })

    if (!response.ok) {
      const data = await response.json().catch(() => ({}))
      throw new Error(data.details || 'Failed to clear field values')
    }

    return response.json()
  },

  // Update stampz configuration
  async updateStampzConfig(id: string, stampzConfig: any): Promise<Template> {
    const response = await fetch(`${API_BASE}/templates/${id}/stampz`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ stampzConfig }),
    })

    if (!response.ok) {
      throw new Error('Failed to update stampz configuration')
    }

    return response.json()
  },

  // Update SHLink placement configuration
  async updateSHLinkConfig(id: string, shlinkConfig: any): Promise<any> {
    const response = await fetch(`${API_BASE}/templates/${id}/shlink`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ shlinkConfig }),
    })
    if (!response.ok) throw new Error('Failed to update SHLink configuration')
    return response.json()
  },

  // Remove SHLink configuration entirely
  async deleteSHLinkConfig(id: string): Promise<any> {
    const response = await fetch(`${API_BASE}/templates/${id}/shlink`, { method: 'DELETE' })
    if (!response.ok) throw new Error('Failed to delete SHLink configuration')
    return response.json()
  },

  // Check if a stampz short URL is already in use
  async checkStampzAvailability(shortUrl: string): Promise<{
    exists: boolean
    templateId?: string
    templateName?: string
    canReuse: boolean
  }> {
    const response = await fetch(`${API_BASE}/stampz/check/${encodeURIComponent(shortUrl)}`)

    if (!response.ok) {
      throw new Error('Failed to check stampz availability')
    }

    return response.json()
  },
}

// Mobile Wizard API
export type RoleMode = 'disabled' | 'basic' | 'wizard'

export interface MobileRoleStatus {
  mode: RoleMode
  wizardStatus: 'idle' | 'processing' | 'completed' | 'failed'
  hasAiHtml: boolean
  requestedAt?: string
  completedAt?: string
  lastEditedAt?: string
  error?: string | null
}

export const mobileApi = {
  async getAnalyses(templateId: string): Promise<any> {
    const response = await fetch(`${API_BASE}/templates/${templateId}/mobile/analyses`)
    if (!response.ok) throw new Error('Failed to get analyses')
    return response.json()
  },

  async triggerAnalysis(templateId: string, opts?: { force?: boolean }): Promise<any> {
    const url = `${API_BASE}/templates/${templateId}/mobile/analyses${opts?.force ? '?force=true' : ''}`
    const response = await fetch(url, {
      method: 'POST',
    })
    if (!response.ok) {
      const body = await response.json().catch(() => null)
      throw new Error(body?.details || body?.error || 'Failed to run analysis')
    }
    return response.json()
  },

  async getStatus(templateId: string): Promise<{ roles: Record<string, MobileRoleStatus> }> {
    const response = await fetch(`${API_BASE}/templates/${templateId}/mobile`)
    if (!response.ok) throw new Error('Failed to get mobile status')
    return response.json()
  },

  async getHtml(
    templateId: string,
    role: string,
    variant?: 'ui' | 'ai',
  ): Promise<{ html: string }> {
    const params = variant ? `?variant=${variant}` : ''
    const response = await fetch(
      `${API_BASE}/templates/${templateId}/mobile/${encodeURIComponent(role)}${params}`,
    )
    if (!response.ok) throw new Error('Failed to get mobile HTML')
    return response.json()
  },

  async setRoleMode(
    templateId: string,
    role: string,
    mode: RoleMode,
  ): Promise<{ mode: RoleMode }> {
    const response = await fetch(
      `${API_BASE}/templates/${templateId}/mobile/${encodeURIComponent(role)}/mode`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mode }),
      },
    )
    if (!response.ok) throw new Error('Failed to set role mode')
    return response.json()
  },

  async requestWizard(
    templateId: string,
    role: string,
  ): Promise<{ mode: string; wizardStatus: string; requestedAt?: string }> {
    const response = await fetch(
      `${API_BASE}/templates/${templateId}/mobile/${encodeURIComponent(role)}/wizard`,
      { method: 'POST' },
    )
    if (!response.ok) {
      const body = await response.json().catch(() => null)
      throw new Error(body?.details || body?.error || 'Failed to request wizard generation')
    }
    return response.json()
  },

  async saveHtml(templateId: string, role: string, html: string): Promise<{ success: boolean }> {
    const response = await fetch(
      `${API_BASE}/templates/${templateId}/mobile/${encodeURIComponent(role)}`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ html }),
      },
    )
    if (!response.ok) throw new Error('Failed to save mobile HTML')
    return response.json()
  },
}

// DEPRECATED: Configuration Management API
export const configApi = {
  async saveConfiguration(
    _templateId: string,
    _config: TemplateConfiguration,
  ): Promise<TemplateConfiguration> {
    throw new Error(
      'configApi.saveConfiguration is deprecated. Use individual namespace endpoints instead.',
    )
  },

  async getConfiguration(_templateId: string): Promise<TemplateConfiguration | null> {
    throw new Error(
      'configApi.getConfiguration is deprecated. Access template.stickers, template.fieldAssignments, etc. directly.',
    )
  },

  async deleteConfiguration(_templateId: string): Promise<void> {
    throw new Error(
      'configApi.deleteConfiguration is deprecated. Use individual namespace endpoints instead.',
    )
  },
}

// Export API
export const exportApi = {
  // Export configured template as PDF
  async exportTemplate(
    templateId: string,
    _format: 'pdf' | 'pdf_with_json' | 'zip' = 'pdf',
  ): Promise<Blob> {
    const template = await templateApi.getTemplate(templateId)

    if (!template) {
      throw new Error('Template not found')
    }

    // For now, just download the original PDF
    const response = await fetch(templateApi.getTemplateDownloadUrl(templateId))

    if (!response.ok) {
      throw new Error('Failed to download template')
    }

    return response.blob()
  },
}

// Program/Stampz Management API
let stampzCache: { data: any[]; timestamp: number } | null = null
const CACHE_DURATION = 5 * 60 * 1000 // 5 minutes

export const programApi = {
  // Get list of existing programs/stamps from qr.md (with caching)
  async getPrograms(): Promise<any[]> {
    // Check cache first
    if (stampzCache && Date.now() - stampzCache.timestamp < CACHE_DURATION) {
      console.log('Using cached stampz data')
      return stampzCache.data
    }
    try {
      console.log('Fetching stampz from https://qr.md/r/stampz')
      const response = await fetch('https://qr.md/r/stampz', {
        method: 'GET',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        mode: 'cors',
      })

      if (!response.ok) {
        console.error(`Stampz API returned status: ${response.status}`)
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const rawPrograms = await response.json()
      console.log(`Received ${Array.isArray(rawPrograms) ? rawPrograms.length : 0} stampz from API`)

      // Transform the actual API response to match our expected format
      if (Array.isArray(rawPrograms)) {
        const transformedData = rawPrograms.map((item) => ({
          id: item.id || '',
          name: item.label || item.bates || 'Unnamed Program',
          shortUrl: item.bates ? `qr.md/${item.bates}` : '',
          fullUrl: item.url || '',
          status: item.status || 'active',
          createdAt: item.created || new Date().toISOString(),
          lastUsed: item.modified || null,
          usageCount: item.pages || 0,
          watermark: '',
          isDemoData: false,
        }))

        // Cache the result
        stampzCache = { data: transformedData, timestamp: Date.now() }
        console.log('Cached fresh stampz data')
        return transformedData
      }

      return []
    }
    catch (error) {
      console.error('Error fetching programs:', error)
      // Return mock data for development/fallback
      const mockData = this.getMockPrograms()
      stampzCache = { data: mockData, timestamp: Date.now() }
      console.log('Cached mock stampz data')
      return mockData
    }
  },

  // Mock data for development/fallback
  getMockPrograms() {
    return [
      {
        id: 'demo_001',
        name: '[DEMO] Diabetes Management Program',
        shortUrl: 'qr.md/demo-dm24',
        fullUrl: 'https://qr.md/r/stampz/demo-diabetes-management-2024',
        status: 'demo',
        createdAt: '2024-01-15T10:00:00Z',
        lastUsed: '2024-08-28T14:30:00Z',
        usageCount: 1247,
        watermark: 'DEMO DATA - NOT FOR PRODUCTION USE',
        isDemoData: true,
      },
      {
        id: 'demo_002',
        name: '[DEMO] Hypertension Care Protocol',
        shortUrl: 'qr.md/demo-htn24',
        fullUrl: 'https://qr.md/r/stampz/demo-hypertension-care-protocol',
        status: 'demo',
        createdAt: '2024-03-10T09:15:00Z',
        lastUsed: '2024-08-27T16:45:00Z',
        usageCount: 892,
        watermark: 'DEMO DATA - NOT FOR PRODUCTION USE',
        isDemoData: true,
      },
    ]
  },

  // Register a new program
  async registerProgram(programData: {
    name: string
    shortName?: string
    description?: string
    templateId: string
  }) {
    try {
      const response = await fetch('https://qr.md/r/stampz', {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          label: programData.name,
          bates: programData.shortName || programData.name.toLowerCase().replace(/\s+/g, '-'),
          metadata: {
            description: programData.description,
            templateId: programData.templateId,
            createdBy: getCurrentUser(),
            createdAt: new Date().toISOString(),
          },
        }),
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const result = await response.json()

      return {
        id: result.id,
        name: result.label || programData.name,
        shortUrl: `qr.md/${result.bates || programData.shortName}`,
        fullUrl: `https://qr.md/r/stampz/demo-${programData.shortName}`,
        status: 'active',
        createdAt: new Date().toISOString(),
        lastUsed: null,
        usageCount: 0,
      }
    }
    catch (error) {
      console.error('Error registering program:', error)
      return {
        id: `demo_${Date.now()}`,
        name: `[DEMO] ${programData.name}`,
        shortUrl: `qr.md/demo-${programData.shortName}`,
        fullUrl: `https://qr.md/r/stampz/demo-${programData.shortName}`,
        status: 'demo',
        createdAt: new Date().toISOString(),
        lastUsed: null,
        usageCount: 0,
        watermark: 'DEMO DATA - NOT FOR PRODUCTION USE',
        isDemoData: true,
      }
    }
  },

  // Update program
  async updateProgram(programId: string, updates: any) {
    try {
      const response = await fetch(`https://qr.md/r/stampz/${programId}`, {
        method: 'PUT',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updates),
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      return response.json()
    }
    catch (error) {
      console.error('Error updating program:', error)
      throw error
    }
  },
}

// FHIR Data API
export const fhirApi = {
  async getSampleBundle(): Promise<{ bundle: any; sourceName: string | null }> {
    const response = await fetch(`${API_BASE}/templates/fhir/sample-bundle`)
    if (!response.ok) {
      throw new Error(`Failed to fetch sample bundle: ${response.statusText}`)
    }
    const sourceNameHeader = response.headers.get('X-Sample-Bundle-Name')
    const bundle = await response.json()
    return { bundle, sourceName: sourceNameHeader ? decodeURIComponent(sourceNameHeader) : null }
  },

  async regenerateQuestionnaire(id: string, opts?: { local?: boolean }): Promise<{ status: string }> {
    const url = `${API_BASE}/templates/${id}/questionnaire/regenerate${opts?.local ? '?local=true' : ''}`
    const response = await fetch(url, {
      method: 'POST',
    })
    if (!response.ok) {
      const data = await response.json().catch(() => ({}))
      throw new Error(data.details || `Failed to regenerate questionnaire: ${response.statusText}`)
    }
    return response.json()
  },

  async getQuestionnaireStatus(id: string): Promise<{ status: string; items?: number; error?: string }> {
    const response = await fetch(`${API_BASE}/templates/${id}/questionnaire/status`)
    if (!response.ok) {
      throw new Error(`Failed to fetch questionnaire status: ${response.statusText}`)
    }
    return response.json()
  },

  async getQuestionnaire(id: string): Promise<any | null> {
    const response = await fetch(`${API_BASE}/templates/${id}/questionnaire`)
    if (!response.ok) {
      if (response.status === 404) return null
      throw new Error(`Failed to fetch questionnaire: ${response.statusText}`)
    }
    return response.json()
  },
}
